/**
 * EvaluationMap — full-page MapLibre view of every site in an
 * evaluation, with trade-area rings, overlap zones, lettered site
 * markers, legend, controls, and demographic toggle.
 *
 *   ┌─────────────────────────────────────────────────────┐
 *   │ ◀ Back  Evaluation name                       │ pill│  top overlays
 *   │                                                     │
 *   │                                          ┌──────────│
 *   │                                          │ Legend   │  right overlay
 *   │                                          │ A …      │
 *   │                                          │ B …      │
 *   │                                          └──────────│
 *   │                                                     │
 *   │                                       ┌─────────────│
 *   │                                       │ Markers ●   │  bottom-right
 *   │                                       │ Rings   ●   │  controls
 *   │                                       │ Overlaps●   │
 *   │                                       │ Demos   ○   │
 *   │                                       └─────────────│
 *   └─────────────────────────────────────────────────────┘
 *
 * Reads sites from `state.evaluationSites` (slice).  Auto-fits to the
 * bbox of every geocoded site on load.
 *
 * Difference vs F6 ComparisonMap: this page renders ALL sites in the
 * evaluation; ComparisonMap renders only the picker subset.
 *
 * Linear: MYD-226 (I5). Closes Phase I.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type maplibregl from 'maplibre-gl'

import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { ChevronLeft, AlertCircle } from 'lucide-react'
import { cn } from 'lib/utils'

import { fetchEvaluationById } from 'slices/evaluationSlice'
import { fetchSites } from 'slices/evaluationSiteSlice'
import { toMappableSites, toPreciseSites } from './utils/siteMapPosition'

import {
	MapContainer,
	TradeAreaRings,
	OverlapZones,
	SiteMarkers,
	MapLegend,
	MapControls,
	DemographicHeatmap,
	tradeAreaService,
	TRADE_AREA_RINGS,
	type SiteForRings,
	type RingVisibilityConfig,
	type SiteForOverlap,
} from 'features/Map'

const STATUS_COLORS: Record<string, string> = {
	DRAFT: 'bg-gray-100 text-gray-700 border-gray-200',
	IN_ANALYSIS: 'bg-blue-100 text-blue-700 border-blue-200',
	UNDER_REVIEW: 'bg-amber-100 text-amber-700 border-amber-200',
	APPROVED: 'bg-green-100 text-green-700 border-green-200',
	REJECTED: 'bg-red-100 text-red-700 border-red-200',
	ARCHIVED: 'bg-gray-200 text-gray-500 border-gray-300',
}

const EvaluationMap = () => {
	const { id } = useParams<{ id: string }>()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const [map, setMap] = useState<maplibregl.Map | null>(null)
	const [isLoaded, setIsLoaded] = useState(false)

	const [showMarkers, setShowMarkers] = useState(true)
	const [showRings, setShowRings] = useState(true)
	const [showOverlaps, setShowOverlaps] = useState(true)
	const [showDemographics, setShowDemographics] = useState(false)

	const evaluation = useSelector((state: any) =>
		id ? state.evaluations?.entities?.[id] : null,
	)
	const { entities: siteEntities, byEvaluation } = useSelector(
		(state: any) => state.evaluationSites,
	)

	useEffect(() => {
		if (!id) return
		;(dispatch as any)(fetchEvaluationById(id))
		;(dispatch as any)(fetchSites(id))
	}, [dispatch, id])

	const sites: any[] = useMemo(() => {
		if (!id) return []
		const ids: string[] = byEvaluation[id] || []
		return ids.map((sid) => siteEntities[sid]).filter(Boolean)
	}, [byEvaluation, siteEntities, id])

	// Same placement rules as the Sites tab — a site whose address could not
	// be geocoded is drawn at the centre of its named city/postal code. Shared
	// helper so the two screens can never disagree on how many pins there are.
	const mappableSites = useMemo(() => toMappableSites(sites), [sites])
	const preciseSites = useMemo(() => toPreciseSites(sites), [sites])
	const approximateCount = mappableSites.length - preciseSites.length
	const unplaceableCount = sites.length - mappableSites.length

	// Rings and overlaps stay on precise sites only: a catchment drawn around
	// a postal-code centroid would look exactly like a measured one.
	const ringSites: SiteForRings[] = useMemo(
		() =>
			preciseSites.map((s) => ({
				id: s._id as string,
				latitude: s.latitude,
				longitude: s.longitude,
			})),
		[preciseSites],
	)
	const overlapSites: SiteForOverlap[] = ringSites
	const ringVisibility: RingVisibilityConfig = useMemo(() => {
		const out: RingVisibilityConfig = {}
		for (const s of preciseSites) {
			out[s._id as string] = TRADE_AREA_RINGS as unknown as number[]
		}
		return out
	}, [preciseSites])

	// Auto-fit on first load + when the placed site list changes. Includes
	// approximate sites so they are inside the initial view.
	useEffect(() => {
		if (!map || !isLoaded || mappableSites.length === 0) return
		const bounds = tradeAreaService.getBoundsForSites(
			mappableSites.map((s) => ({ longitude: s.longitude, latitude: s.latitude })),
		)
		map.fitBounds(bounds as maplibregl.LngLatBoundsLike, {
			padding: 80,
			duration: 600,
			maxZoom: 13,
		})
	}, [map, isLoaded, mappableSites])

	const handleBack = useCallback(() => {
		if (id) navigate(`/evaluations/${id}`)
		else navigate(-1)
	}, [navigate, id])

	const status = evaluation?.status as string | undefined

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN
				mobileOpen={mobileMenuOpen}
				onMobileClose={() => setMobileMenuOpen(false)}
			/>

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<main className="flex-1 relative overflow-hidden">
					{/* Full-bleed map */}
					<MapContainer
						className="!rounded-none"
						onMapLoad={(m) => {
							setMap(m)
							setIsLoaded(true)
						}}
					>
						{showRings && (
							<TradeAreaRings
								map={map}
								isMapLoaded={isLoaded}
								sites={ringSites}
								visibility={ringVisibility}
								visible={showRings}
							/>
						)}
						<OverlapZones
							map={map}
							isMapLoaded={isLoaded}
							sites={overlapSites}
							visible={showOverlaps}
						/>
						{showMarkers && <SiteMarkers map={map} sites={mappableSites} />}
						<DemographicHeatmap
							map={map}
							isMapLoaded={isLoaded}
							visible={showDemographics}
						/>
					</MapContainer>

					{/* Top-left: back + evaluation name */}
					<div className="absolute top-4 left-4 z-20 flex items-center gap-2 max-w-[60%]">
						<button
							type="button"
							onClick={handleBack}
							className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white/95 backdrop-blur border border-gray-200 text-gray-600 shadow-sm hover:text-[#0080fe] hover:border-[#0080fe] transition-colors"
						>
							<ChevronLeft className="w-3.5 h-3.5" />
							Back
						</button>
						{evaluation?.name && (
							<div className="px-3 py-1.5 rounded-md bg-white/95 backdrop-blur border border-gray-200 shadow-sm text-sm font-semibold text-gray-900 truncate">
								{evaluation.name}
							</div>
						)}
					</div>

					{/* Top-right: status badge */}
					{status && (
						<div className="absolute top-4 right-4 z-20">
							<span
								className={cn(
									'inline-block text-[10px] uppercase tracking-wide font-semibold px-2.5 py-1.5 rounded-md border shadow-sm backdrop-blur',
									STATUS_COLORS[status] ?? STATUS_COLORS.DRAFT,
								)}
							>
								{status.replace(/_/g, ' ')}
							</span>
						</div>
					)}

					{/* Placement warnings */}
					{(approximateCount > 0 || unplaceableCount > 0) && (
						<div className="absolute top-16 left-4 z-20 flex flex-col gap-1">
							{approximateCount > 0 && (
								<div className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50/95 backdrop-blur border border-amber-200 px-2 py-1 rounded shadow-sm">
									<AlertCircle className="w-3 h-3" />
									{approximateCount} site{approximateCount === 1 ? '' : 's'} shown at
									approximate location{approximateCount === 1 ? '' : 's'} (dashed pin) — no
									trade area
								</div>
							)}
							{unplaceableCount > 0 && (
								<div className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 bg-gray-50/95 backdrop-blur border border-gray-200 px-2 py-1 rounded shadow-sm">
									<AlertCircle className="w-3 h-3" />
									{unplaceableCount} site{unplaceableCount === 1 ? '' : 's'} hidden — no
									address or city to place {unplaceableCount === 1 ? 'it' : 'them'} by
								</div>
							)}
						</div>
					)}

					{/* Right: legend */}
					{mappableSites.length > 0 && (
						<MapLegend
							sites={mappableSites}
							map={map}
							className="absolute top-16 right-4 z-20 max-w-[260px] max-h-[60vh] overflow-y-auto"
						/>
					)}

					{/* Bottom-right: controls */}
					<MapControls
						className="absolute bottom-4 right-4 z-20"
						orientation="vertical"
						title="Layers"
						controls={[
							{
								key: 'markers',
								label: 'Markers',
								active: showMarkers,
								onToggle: () => setShowMarkers((v) => !v),
							},
							{
								key: 'rings',
								label: 'Rings',
								active: showRings,
								onToggle: () => setShowRings((v) => !v),
							},
							{
								key: 'overlaps',
								label: 'Overlaps',
								active: showOverlaps,
								onToggle: () => setShowOverlaps((v) => !v),
							},
							{
								key: 'demographics',
								label: 'Demographics',
								active: showDemographics,
								onToggle: () => setShowDemographics((v) => !v),
							},
						]}
					/>

					{/* Empty state when no sites at all */}
					{sites.length === 0 && evaluation && (
						<div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
							<div className="pointer-events-auto rounded-md border border-gray-200 bg-white/95 backdrop-blur shadow-sm px-4 py-3 max-w-sm text-center">
								<p className="text-sm font-semibold text-gray-900 mb-1">
									No sites in this evaluation
								</p>
								<p className="text-xs text-gray-500">
									Add sites in the Sites tab. Once geocoded they appear here on the map.
								</p>
							</div>
						</div>
					)}
				</main>
			</div>
		</div>
	)
}

export default EvaluationMap
